// pages/Products/AllProducts.js
import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import Breadcrumb from "components/Common/Breadcrumb";
import TableContainer from "components/Common/DataTableContainer";
import DashboardLoader from "components/Loader/DashboardLoader";
import UpdateMRPModal from "components/Products/UpdateMRPModal";
import DeleteProductModal from "components/Products/DeleteProductModal";
import { productApiService } from "services/productApiService";

const AllProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch all products
  const fetchProducts = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);

    try {
      const data = await productApiService.getAllProducts();
      setProducts(data);
    } catch (error) {
      toast.error("Failed to fetch products");
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Handle MRP update
  const handleUpdateMRP = async (newMrp) => {
    if (!selectedProduct) return;

    try {
      await productApiService.updateProductMRP(selectedProduct.id, newMrp);
      toast.success("Product MRP updated successfully");

      // Update local state
      setProducts(prevProducts =>
        prevProducts.map(product =>
          product.id === selectedProduct.id
            ? { ...product, mrp: newMrp }
            : product
        )
      );

      setUpdateModalOpen(false);
    } catch (error) {
      toast.error("Failed to update MRP");
      console.error("Error updating MRP:", error);
      throw error;
    }
  };

  // Handle product deletion
  const handleDeleteProduct = async () => {
    if (!selectedProduct) return;

    try {
      await productApiService.deleteProduct(selectedProduct.id);
      toast.success("Product deleted successfully");

      // Remove from local state
      setProducts(prevProducts =>
        prevProducts.filter(product => product.id !== selectedProduct.id)
      );

      setDeleteModalOpen(false);
    } catch (error) {
      toast.error("Failed to delete product");
      console.error("Error deleting product:", error);
      throw error;
    }
  };

  // Handle edit MRP click
  const handleEditMRPClick = (product) => {
    setSelectedProduct(product);
    setUpdateModalOpen(true);
  };

  // Handle delete click
  const handleDeleteClick = (product) => {
    setSelectedProduct(product);
    setDeleteModalOpen(true);
  };

  // Get unique categories for filter
  const uniqueCategories = useMemo(() => {
    const categories = products.map(product => product.category);
    return [...new Set(categories)].filter(Boolean);
  }, [products]);

  // Filter products
  const filteredProducts = useMemo(() => {
    if (!products.length) return [];

    return products.filter(product => {
      // Search filter
      const matchesSearch = !searchQuery ||
        product.productname.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.productbrand.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.vandor.toLowerCase().includes(searchQuery.toLowerCase());

      // Category filter
      const matchesCategory = !categoryFilter || product.category === categoryFilter;

      // Status filter
      const matchesStatus = !statusFilter ||
        (statusFilter === "available" && product.availablestatus) ||
        (statusFilter === "unavailable" && !product.availablestatus);

      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [products, searchQuery, categoryFilter, statusFilter]);

  // Format currency
  const formatCurrency = (amount) => {
    return `৳${parseFloat(amount || 0).toLocaleString('en-BD')}`;
  };

  // Get status badge
  const getStatusBadge = (status) => {
    return status ? (
      <span className="badge bg-success">
        <i className="fas fa-check me-1"></i> Available
      </span>
    ) : (
      <span className="badge bg-danger">
        <i className="fas fa-times me-1"></i> Unavailable
      </span>
    );
  };

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => `#${row.original.id}`,
      },
      {
        header: "Image",
        accessorKey: "imagelink",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="d-flex justify-content-center">
            {row.original.imagelink ? (
              <img
                src={row.original.imagelink}
                alt={row.original.productname}
                className="rounded"
                style={{ width: "50px", height: "50px", objectFit: "cover" }}
                onError={(e) => {
                  e.target.src = "/static/images/default-product.png";
                  e.target.onerror = null;
                }}
              />
            ) : (
              <div className="rounded bg-light d-flex align-items-center justify-content-center"
                style={{ width: "50px", height: "50px" }}>
                <i className="fas fa-box text-muted"></i>
              </div>
            )}
          </div>
        ),
      },
      {
        header: "Product Details",
        accessorKey: "productname",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="fw-semibold">{row.original.productname}</div>
            <small className="text-muted d-block">
              {row.original.productbrand} • {row.original.size}
            </small>
            {/* <small className="d-block">
              <span className="badge bg-info me-1">{row.original.category}</span>
              <span className="badge bg-secondary">{row.original.subcategory}</span>
            </small> */}
          </div>
        ),
      },
      {
        header: "Vendor",
        accessorKey: "vandor",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div>{row.original.vandor}</div>
            <small className="text-muted">{row.original.origin}</small>
          </div>
        ),
      },
      {
        header: "Packaging",
        accessorKey: "packaging",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">
            <span className="badge bg-warning">{row.original.packaging}</span>
            <div className="mt-1">
              <small className="text-muted">Qty: {row.original.quantity}</small>
            </div>
          </div>
        ),
      },
      {
        header: "MRP",
        accessorKey: "mrp",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="fw-bold text-primary">
            {formatCurrency(row.original.mrp)}
          </div>
        ),
      },
      {
        header: "Status",
        accessorKey: "availablestatus",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => getStatusBadge(row.original.availablestatus),
      },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="d-flex flex-column gap-2">
            <div className="d-flex gap-1">
              <button
                onClick={() => handleEditMRPClick(row.original)}
                className="btn btn-primary btn-sm"
                title="Update MRP"
              >
                <i className="fas fa-edit"></i>
              </button>
              <button
                onClick={() => handleDeleteClick(row.original)}
                className="btn btn-danger btn-sm"
                title="Delete Product"
              >
                <i className="fas fa-trash"></i>
              </button>
            </div>

          </div>
        ),
      },
    ],
    []
  );

  // Statistics
  const totalProducts = products.length;
  const availableProducts = products.filter(p => p.availablestatus).length;
  const totalMRPValue = products.reduce((sum, product) =>
    sum + parseFloat(product.mrp || 0), 0
  );
  const averageMRP = totalProducts > 0 ? totalMRPValue / totalProducts : 0;

  if (loading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Products" breadcrumbItem="All Products" />

        {/* Statistics Cards */}
        {/* <div className="row mb-4">
          <div className="col-md-3">
            <div className="card border-primary border-opacity-25">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0">Total Products</p>
                    <h4 className="mt-2">{totalProducts}</h4>
                  </div>
                  <div className="avatar-sm">
                    <span className="avatar-title bg-primary rounded-circle">
                      <i className="fas fa-boxes font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-success border-opacity-25">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0">Available Products</p>
                    <h4 className="mt-2">{availableProducts}</h4>
                  </div>
                  <div className="avatar-sm">
                    <span className="avatar-title bg-success rounded-circle">
                      <i className="fas fa-check-circle font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-warning border-opacity-25">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0">Total MRP Value</p>
                    <h4 className="mt-2">{formatCurrency(totalMRPValue)}</h4>
                  </div>
                  <div className="avatar-sm">
                    <span className="avatar-title bg-warning rounded-circle">
                      <i className="fas fa-money-bill-wave font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-info border-opacity-25">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0">Average MRP</p>
                    <h4 className="mt-2">{formatCurrency(averageMRP.toFixed(2))}</h4>
                  </div>
                  <div className="avatar-sm">
                    <span className="avatar-title bg-info rounded-circle">
                      <i className="fas fa-calculator font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* Search and Filter Bar */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search products by name, brand, or vendor..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => setSearchQuery("")}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>

              <div className="col-md-3">
                <select
                  className="form-select"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {uniqueCategories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="available">Available</option>
                  <option value="unavailable">Unavailable</option>
                </select>
              </div>

              <div className="col-md-2">
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-success"
                    onClick={() => fetchProducts(false)}
                    disabled={refreshing}
                    title="Refresh Products"
                  >
                    <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`}></i>
                  </button>
                  <button
                    className="btn btn-light"
                    onClick={() => {
                      setSearchQuery("");
                      setCategoryFilter("");
                      setStatusFilter("");
                    }}
                    disabled={!searchQuery && !categoryFilter && !statusFilter}
                  >
                    <i className="fas fa-times me-1"></i>
                    Clear
                  </button>
                </div>
              </div>
            </div>

            {/* <div className="mt-3">
              <small className="text-muted">
                Showing {filteredProducts.length} of {products.length} products
                {searchQuery && ` • Searching: "${searchQuery}"`}
                {categoryFilter && ` • Category: ${categoryFilter}`}
                {statusFilter && ` • Status: ${statusFilter}`}
              </small>
            </div> */}
          </div>
        </div>

        {/* Products Table */}
        <div className="card">
          <div className="card-body">
            {filteredProducts.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-box-open fa-3x text-muted mb-3"></i>
                <h4>No products found</h4>
                <p className="text-muted">
                  {searchQuery || categoryFilter || statusFilter
                    ? 'Try adjusting your search filters'
                    : 'No products available in the system'}
                </p>
              </div>
            ) : (
              <TableContainer
                columns={columns}
                data={filteredProducts}
                isGlobalFilter={false}
                isPagination={true}
                pagination="pagination"
                paginationWrapper="dataTables_paginate paging_simple_numbers"
                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                isCustomPageSize={true}
              />
            )}
          </div>
        </div>

        {/* Modals */}
        <UpdateMRPModal
          isOpen={updateModalOpen}
          toggle={() => setUpdateModalOpen(false)}
          product={selectedProduct}
          onUpdate={handleUpdateMRP}
        />

        <DeleteProductModal
          isOpen={deleteModalOpen}
          toggle={() => setDeleteModalOpen(false)}
          product={selectedProduct}
          onDelete={handleDeleteProduct}
        />
      </div>
    </div>
  );
};

export default AllProducts;